/**
 * The pieces every /pressure-testing screen draws the same way.
 *
 * The panel, dialog, field and input idiom are the downhole screens' own
 * (pages/downhole/DownholeBits.tsx) — the re-skin standard for a vertical
 * register — IMPORTED, not copied and not edited, so the two verticals stay one
 * shape. Only what is Pressure Testing's own lives here.
 */
import React from 'react';
import { ptStatusSkin, type PtStatusSkin } from '../../lib/pressure-testing';

export { DhDialog as PtDialog, DhEmpty as PtEmpty, DhField as PtField, DhPanel as PtPanel, DhSpec as PtSpec } from '../downhole/DownholeBits';
export { DH_BAND_SECONDARY as PT_BAND_SECONDARY, DH_INPUT as PT_INPUT, DH_SECONDARY as PT_SECONDARY } from '../downhole/DownholeBits';

/** A saturated badge, dark ink on the fill. */
export function PtSkinBadge({ skin, testId, value }: { skin: PtStatusSkin; testId?: string; value?: string | null }) {
  return (
    <span
      data-testid={testId}
      data-value={value ?? ''}
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

/** The API's calibration_status as a saturated badge, dark ink on the fill. */
export function PtStatusBadge({ status }: { status: string | null | undefined }) {
  const skin = ptStatusSkin(status);
  return (
    <span
      data-status={status ?? ''}
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

/** One filter pill: amber-filled when active, its count beside it (none while loading). */
export function PtChip({
  label,
  active,
  count,
  onClick,
}: {
  label: string;
  active: boolean;
  count?: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
        active
          ? 'bg-fi-amber text-fi-amber-ink'
          : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
      }`}
    >
      {label}
      {count !== undefined && (
        <span className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>{count}</span>
      )}
    </button>
  );
}

/** A write's error, from the API's `error` string when it sent one. */
export function ptErrorMessage(err: unknown, fallback: string): string {
  const msg = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  return typeof msg === 'string' && msg ? msg : fallback;
}
